import { Component, inject, signal, computed, effect, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';

import { finalize, map } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  ReservationConfirmationParams,
  ReservationPut,
} from './../../core/interfaces/reservation-payload.interface';
import { ReservationService } from '../../core/services/reservation.service';
import { ECancelReason } from '../../core/enum/ECancelReason';
import { EOriginStatus } from '../../core/enum/EOriginStatus';

@Component({
    selector: 'madero-confirm-reservation',
    templateUrl: './confirm-reservation.component.html',
    styleUrl: './confirm-reservation.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon, MatProgressSpinnerModule, CommonModule]
})
export class ConfirmReservationComponent {
  readonly #route = inject(ActivatedRoute);
  readonly #reservationService = inject(ReservationService);
  readonly #snackBar = inject(MatSnackBar);

  readonly loading = signal(true);
  readonly errorService = signal(false);
  readonly confirmationParams = toSignal(
    this.#route.queryParams.pipe(
      map((params): ReservationConfirmationParams => {
        const confirmation = params as ReservationConfirmationParams;
        return { ...confirmation, date: this.parseDatePtBR(confirmation.date) };
      })
    ),
    {
      initialValue: null,
    }
  );
  readonly isConfirmed = computed(() => {
    const confirmation = this.confirmationParams();

    if (confirmation === null || this.errorService()) return null;

    switch (confirmation.status) {
      case EOriginStatus.CONFIRMED:
        return true;
      case EOriginStatus.CANCELED:
        return false;
      default:
        return null;
    }
  });

  constructor() {
    effect(
      () => {
        const body = this.confirmationParams();
        if (body !== null) this.confirmReservation(body);
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  private parseDatePtBR(dateString: string): string {
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  }

  confirmReservation(body: ReservationConfirmationParams): void {
    const reservation: ReservationPut = {
      ...body,
      // Add a cancelReason property if is CANCELED status
      ...(body.status === EOriginStatus.CANCELED && { cancelReason: ECancelReason.CLIENT }),
    };

    this.loading.set(true);
    this.#reservationService
      .putReservation(reservation)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        error: (e) => {
          console.error(e);
          this.errorService.set(true);
          e.error?.errors.forEach((error: any) => {
            this.#snackBar.open(error.messageCustomer, 'Fechar');
          });
        },
      });
  }
}
